import type { TranslationCatalog } from "../types.js";
import { pluralSk } from "../plural.js";

function coinWord(n: number): string {
  return pluralSk(n, ["minca", "mince", "mincí"]);
}

export default {
  "common.loading": "Pripájanie...",
  "common.loadingEllipsis": "Načítavanie...",
  "common.unknownError": "Neznáma chyba",
  "common.errorPrefix": (p: { message: string }) => `Chyba: ${p.message}`,
  "common.retry": "Skús znova",
  "common.close": "Zavrieť",
  "common.back": "← Späť",
  "common.cancel": "Zrušiť",
  "common.details": "Detaily",
  "common.settingsTitle": "⚙️ Nastavenia",
  "common.profile": "👤 Profil",
  "common.rules": "📖 Pravidlá",

  "login.subtitle": "Prihlás sa a hraj.",
  "login.continueFacebook": "Pokračovať cez účet Facebook",
  "login.continueGoogle": "Pokračovať cez účet Google",
  "login.continueEmail": "Pokračovať s e-mailovou adresou",
  "login.tagline": "Kartová hra s tromfami a licitáciou pre troch hráčov. Licituj, vyber tromf a ber štichy — kto prvý dosiahne 101 bodov, vyhráva.",
  "login.linkRules": "Pravidlá",
  "login.linkStrategy": "Stratégia",
  "login.linkAbout": "O hre",
  "login.linkPrivacy": "Súkromie",

  "lobby.entryFee": (p: { fee: number }) => `Vstupné za partiu: ${p.fee} ${coinWord(p.fee)}`,
  "lobby.resumeMatch": "Pokračovať v hre",
  "lobby.playButton": "Hrať (3 hráči)",
  "lobby.inviteFriend": "📣 Pozvať priateľa",
  "lobby.insufficientFunds": (p: { missing: number }) => `Nemáš dosť mincí na vstupné (chýba ${p.missing}).`,
  "lobby.watchAd": "Pozri reklamu za ďalšie mince",

  "leaderboard.title": "🏆 Rebríček",
  "leaderboard.emptySeason": "Túto sezónu zatiaľ nie sú žiadni hráči v rebríčku (treba odohrať aspoň 1 partiu).",
  "leaderboard.emptyGlobal": "Zatiaľ nie sú žiadni hráči v rebríčku (treba odohrať aspoň 1 partiu celkovo).",
  "leaderboard.seasonTabDefault": "Sezóna",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Sezóna ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 Celkovo (všetky sezóny)",
  "leaderboard.colPlayer": "Hráč",
  "leaderboard.colPoints": "Body",
  "leaderboard.colMatches": "Partie",
  "leaderboard.you": "Ty",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Tvoje skóre: ${p.points} bodov z ${p.matchesPlayed} partií (na zaradenie do rebríčka treba aspoň 1 partiu)`,

  "rules.title": "📖 Ako sa hrá Meksiko",
  "rules.cardsHeading": "Karty",
  "rules.cardsBody":
    "Hrá sa s 32 kartami (7, 8, 9, 10, J, Q, K, A vo všetkých 4 farbách). Sila kariet v rámci farby, od najslabšej: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. Tromfová farba prebíja všetky netromfové karty bez ohľadu na hodnotu.",
  "rules.dealingHeading": "Rozdávanie",
  "rules.dealingBody": 'Každý z 3 hráčov dostane 10 kariet (v dvoch kolách po 5), a 2 karty zostávajú skryté v "talóne".',
  "rules.biddingHeading": "Licitácia",
  "rules.biddingBody":
    'Prvý hráč musí licitovať aspoň 5. Každý ďalší musí licitovať prísne vyššie (5, 6, 7, 8, 9, 10, alebo "MEKSIKO") alebo povedať "ďalej" — kto raz povie "ďalej", natrvalo vypadáva z licitácie pre toto rozdanie. Licitácia sa končí, keď zostane iba jeden hráč.',
  "rules.talonHeading": "Talón a odhoz",
  "rules.talonBody":
    "Víťaz licitácie otvorí talón (2 karty, viditeľné pre všetkých), pridá ich do svojej ruky (teraz má 12 kariet), potom odhodí 2 karty lícom nadol. Následne si vyberie tromfovú farbu.",
  "rules.mexicoHeading": "MEKSIKO (20)",
  "rules.mexicoBody":
    'Kto licituje "MEKSIKO", zaväzuje sa vziať všetkých 10 zdvihov. Hrá sa bez tromfu a bez talónu — iba s pôvodnými 10 kartami.',
  "rules.tricksHeading": "Priebeh hry (zdvihy)",
  "rules.tricksBody":
    "Musíš priznať vyloženú farbu, ak ju máš. Ak ju nemáš, musíš hodiť tromf, ak ho máš (rezanie) — iba ak nemáš ani jedno ani druhé, môžeš hodiť čokoľvek. Zdvih berie najsilnejšia karta vyloženej farby, pokiaľ niekto nehodil tromf — vtedy berie najsilnejší tromf, bez ohľadu na farbu. Kto vezme zdvih, začína ďalší.",
  "rules.scoringHeading": "Bodovanie",
  "rules.scoringBody":
    "Víťaz licitácie: ak vezme aspoň toľko zdvihov, koľko licitoval, získa toľko bodov, koľko skutočne vzal. Ak sa mu to nepodarí, získa <strong>záporné</strong> body vo výške licitácie. Ostatní dvaja hráči vždy získajú body rovné počtu zdvihov, ktoré vzali.",
  "rules.matchEndHeading": "Koniec partie",
  "rules.matchEndBody":
    "Partia sa končí, keď niekto dosiahne cieľové skóre. Ak viacero hráčov prekročí cieľ v tom istom rozdaní s rovnakým najvyšším skóre, hrá sa ďalšie rozdanie, kým sa to nevyrieši.",

  "settings.soundOff": "🔇 Zvuk vypnutý",
  "settings.soundOn": "🔊 Zvuk zapnutý",
  "settings.displayNameLabel": "Meno v hre",
  "settings.saveDisplayName": "Uložiť meno",
  "settings.displayNameLengthError": "Meno musí mať 1-20 znakov.",
  "settings.saveFailed": "Uloženie zlyhalo, skús znova.",
  "settings.logout": "🚪 Odhlásiť sa",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Čakanie na hráčov... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `Zostáva ${p.remainingSec}s, kým AI zaplní prázdne miesta`,

  "game.passLabel": "ďalej",
  "game.passButton": "Ďalej",
  "game.mexicoLabel": "MEKSIKO",
  "game.bidLabel": (p: { bid: string }) => `Licitácia: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Tromf: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Zdvihy: ${p.count}`,
  "game.biddingInProgress": "Prebieha licitácia...",
  "game.yourBid": "Tvoja licitácia",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} odhadzuje karty...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} vyberá tromf...`,
  "game.chooseTrumpPrompt": "Vyber tromf",
  "game.nextDealPause": "Ďalšie rozdanie...",
  "game.fromTalon": "z talónu",
  "game.talonRevealLabel": (p: { bidder: string }) => `Karty z talónu (${p.bidder})`,
  "game.yourTurnLabel": "Si na rade",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}s`,
  "game.idleBotTakeover": "🤖 Ťah si nestihol/-la odohrať včas, takže teraz za teba hrá AI. Klikni kamkoľvek a prevezmi kontrolu späť.",
  "game.leaveMatch": "Opustiť partiu",
  "game.cardAlt": "karta",
  "game.botDisplayName": "AI hráč",
  "game.botBadgeIdle": "🤖 AI (dočasne)",
  "game.botBadgeFull": "🤖 BOT",
  "game.declarerTooltip": "Vedie licitáciu",

  "matchEnd.title": "Partia skončila",
  "matchEnd.winner": (p: { name: string }) => `Víťaz: ${p.name}`,
  "matchEnd.backToLobby": "Späť do lobby",

  "reactions.sendButton": "Poslať reakciu",
  "reaction.angry_small_card": "Prečo malou kartou?",
  "reaction.angry_big_card": "Prečo veľkou kartou?",
  "reaction.bravo": "Bravo",
  "reaction.svaka_cast": "Klobúk dole",
  "reaction.cestitam_pobeda": "Gratulujem k výhre",
  "reaction.izvini": "Prepáč",
  "reaction.pa_zasto": "Ale prečo!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Sezóna",
  "medal.season_silver": "🥈 Sezóna",
  "medal.season_bronze": "🥉 Sezóna",
  "medal.first_match": "🎉 Prvá partia",
  "medal.matches_100": "💯 100 partií",
  "medal.mexico_master": "🌵 Majster Meksika",
  "medal.hat_trick_3_wins": "🔥 Hetrik (3 výhry)",
  "medal.tricks_1000": "🃏 1000 zdvihov",

  "suit.diamonds": "Káro",
  "suit.hearts": "Srdce",
  "suit.clubs": "Tref",
  "suit.leaves": "List",

  "profile.matchesPlayed": "Odohraných partií",
  "profile.firstPlace": (p: { wins: number }) => `🥇 1. miesto (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 2. miesto (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 3. miesto (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Bodovanie za partiu: 1. miesto ${p.win}, 2. miesto ${p.second}, 3. miesto ${p.third} b.`,
  "profile.noMedals": "Zatiaľ žiadne získané medaily.",
  "profile.medalsHeading": "Medaily",
  "profile.seasonHistoryHeading": "História sezón",
  "profile.colSeason": "Sezóna",
  "profile.colRank": "Poradie",
  "profile.deleteAccountButton": "🗑️ Zmazať účet",
  "profile.deleteAccountTitle": "Zmazať účet",
  "profile.deleteAccountWarning":
    "Toto natrvalo zmaže tvoj účet a všetky údaje (históriu zápasov, rebríček, medaily, zostatok mincí). Túto akciu nemožno vrátiť späť.",
  "profile.deleteAccountConfirm": "Áno, zmazať účet",
  "profile.deleteAccountFailed": "Zmazanie zlyhalo, skús znova.",
  "profile.deleteAccountActiveMatch": "Účet nemôžeš zmazať, kým si v aktívnom zápase. Dokonči alebo opusti zápas a skús to znova.",

  "errors.NOT_YOUR_TURN": "Nie si na rade.",
  "errors.INVALID_BID": "Neplatná licitácia.",
  "errors.BID_TOO_LOW": "Licitácia musí byť vyššia ako aktuálne najvyššia.",
  "errors.MUST_BID_FIRST": "Prvý hráč musí licitovať aspoň 5, nemôže povedať ďalej.",
  "errors.DUPLICATE_DISCARD": "Nemôžeš odhodiť tú istú kartu dvakrát.",
  "errors.CARD_NOT_IN_HAND": "Táto karta nie je v tvojej ruke.",
  "errors.MUST_FOLLOW_SUIT": "Musíš priznať farbu alebo rezať tromfom, ak nemôžeš.",
  "errors.TRICK_INCOMPLETE": "Zdvih ešte nie je dokončený.",
  "errors.MATCH_ENDED": "Partia už skončila.",
  "errors.UNKNOWN_ACTION": "Neznáma akcia.",
  "errors.WRONG_PHASE": "Táto akcia nie je povolená v tejto fáze hry.",
  "errors.NOT_BID_WINNER": "Toto môže urobiť iba víťaz licitácie.",
  "errors.MATCH_NOT_ENDED": "Partia ešte neskončila.",
  "errors.invalid_session_token": "Relácia vypršala, prihlás sa znova.",
  "errors.room_full": "Stôl je plný, skús to znova o chvíľu.",
  "errors.insufficient_balance": "Nemáš dosť mincí na vstupné.",
  "errors.ad_unavailable": "Reklama momentálne nie je dostupná. Skontroluj internetové pripojenie (alebo vypni AdBlock) a skús to znova.",
  "errors.invalid_or_early_ticket": "Reklama nebola rozpoznaná ako pozretá. Skús to znova.",
  "errors.daily_ad_limit_reached": "Dosiahol si denný limit reklám. Skús to zajtra.",

  "share.inviteText": "Zahraj si Meksiko so mnou!",
  "ad.placeholderText": "Simulovaná reklama (standalone webové demo)",
  "rotateOverlay.text": "Otoč telefón, aby si mohol hrať Meksiko",
  "languageSwitcher.ariaLabel": "Jazyk",
} satisfies TranslationCatalog;
